import React, { useState } from "react"
import { Eye, Star, Linkedin, Phone, Mail, User } from "lucide-react"
import { ContactAttributes } from "@/interface/searchTable/search"
import CompanyIcon from "../../static/media/avatars/phoenix_avatar.svg?react"
import LinkedinIcon from "../../static/media/icons/linkedin_icon.svg?react"

import { Button } from "./button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip"
import { useAppDispatch } from "@/app/hooks/reduxHooks"
import { openCompanyDetails } from "@/features/searchTable/slice/companyDetailsSlice"

interface ContactCardProps {
  contact: ContactAttributes
  location?: string
  secondaryPhone?: string
}

const ContactCard: React.FC<ContactCardProps> = ({ contact, location = "California, US", secondaryPhone }) => {
  const [revealedFields, setRevealedFields] = useState<Set<string>>(new Set())
  const dispatch = useAppDispatch()

  const toggleReveal = (field: string) => {
    const newRevealed = new Set(revealedFields)
    if (newRevealed.has(field)) {
      newRevealed.delete(field)
    } else {
      newRevealed.add(field)
    }
    setRevealedFields(newRevealed)
  }

  const revealAllInfo = () => {
    setRevealedFields(new Set(["phone", "secondaryPhone", "email"]))
  }

  const maskEmail = (email: string) => {
    if (!email) return ""
    const [username, domain] = email.split("@")
    const maskedUsername = "• • • •"
    return `${maskedUsername} @${domain}`
  }

  const maskPhone = (phone: string) => {
    if (!phone) return ""
    const lastFour = phone.slice(-4)
    return `• • • • ${lastFour}`
  }

  const displayName =
    contact.full_name ||
    (contact.first_name && contact.last_name
      ? `${contact.first_name} ${contact.last_name}`
      : contact.first_name || contact.last_name || "Unknown Contact")

  const jobTitle = contact.title && contact.company ? `${contact.title}, ${contact.company}` : contact.title || contact.company || ""

  return (
    <div className="mx-auto w-[440px] rounded-2xl border border-gray-100 bg-white pb-[23px] shadow-lg">
      {/* Header Section */}
      <div className="px-5 ">
        <div className="flex items-start justify-between border-b pb-4 pt-5">
          <div className="flex-1">
            <span className="mb-1 text-base font-medium text-gray-950">{displayName}</span>
            {!!jobTitle && <p className="mb-1 text-sm font-normal text-gray-600">{jobTitle}</p>}
            <p className="text-sm font-normal text-gray-600">{location}</p>
          </div>
          <div className="ml-4 rounded-full bg-gray-800 p-3">
            <CompanyIcon className="size-5" />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mb-2 flex w-full flex-row items-center justify-end gap-3 px-5 py-[10px] ">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button onClick={revealAllInfo} className="rounded-lg bg-[#335CFF] p-2 text-sm font-medium hover:bg-[#335CFF]">
                <Eye className="size-5" />
                Reveal info
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Reveal contact will consume 5 credits</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button
          className="rounded-lg border border-[#335CFF] bg-transparent p-2 text-sm font-medium text-[#335CFF] hover:bg-transparent"
          onClick={() => dispatch(openCompanyDetails(null))}
        >
          <User className="text-[#335CFF]" />
          View profile
        </Button>
        {!!contact.linkedin_url && (
          <a
            href={contact.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center rounded-xl border border-gray-200 bg-transparent p-3 text-blue-600 transition-colors hover:bg-gray-50"
          >
            <Button className="size-3 bg-transparent p-2 hover:bg-transparent">
              <LinkedinIcon className="size-5" />
            </Button>
          </a>
        )}
      </div>

      {/* Contact Information */}
      <div className="space-y-6">
        {/* Phone Section */}
        {!!(contact.phone_number || secondaryPhone) && (
          <div>
            <div className="mb-2 bg-[#F7F7F7] px-5 py-1.5">
              <span className="text-xs font-medium text-[#A3A3A3]">PHONE</span>
            </div>
            <div className="space-y-3 px-5 py-1">
              {!!contact.phone_number && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Phone className="size-[18px] text-gray-600" />
                    <span className="text-sm font-normal text-[#5C5C5C]">Primary Phone</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-950">
                      {revealedFields.has("phone") ? contact.phone_number : maskPhone(contact.phone_number)}
                    </span>
                    <button onClick={() => toggleReveal("phone")} className="text-blue-500 transition-colors hover:text-blue-600">
                      <Eye className="size-[18px]" />
                    </button>
                  </div>
                </div>
              )}
              {!!secondaryPhone && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Phone className="size-[18px] text-gray-600" />
                    <span className="text-gray-600">Secondary Phone</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-950">
                      {revealedFields.has("secondaryPhone") ? secondaryPhone : maskPhone(secondaryPhone)}
                    </span>
                    <button onClick={() => toggleReveal("secondaryPhone")} className="text-blue-500 transition-colors hover:text-blue-600">
                      <Eye className="size-[18px]" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email Section */}
        {!!contact.email && (
          <div>
            <div className="mb-2 bg-[#F7F7F7] px-5 py-1.5">
              <span className="text-xs font-medium text-[#A3A3A3]">EMAIL</span>
            </div>
            <div className="flex items-center justify-between px-5">
              <div className="flex items-center gap-3 ">
                <Mail className="size-[18px] text-gray-600" />
                <span className="text-sm font-normal text-[#5C5C5C]">Official Email</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-950">{revealedFields.has("email") ? contact.email : maskEmail(contact.email)}</span>
                <button onClick={() => toggleReveal("email")} className="text-blue-500 transition-colors hover:text-blue-600">
                  <Eye className="size-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ContactCard
